import classNames               from 'classnames';
import * as React               from 'react';
import { useTranslation }       from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import './scss/style.scss';

import needReset         from '../../common/tools/need-reset';
import { useCatalog }    from '../../providers/catalog';
import { useCommon }     from '../../providers/common';
import { useNavigation } from '../../providers/navigation';
import { useSidebar }    from '../../providers/sidebar';
import sidebarConstants  from '../../providers/sidebar/sidebar.constants';
import { useUser }       from '../../providers/user';

import DealerIcon   from '../icons/DealerIcon';
import DiscountIcon from '../icons/DiscountIcon';
import PhoneIcon    from '../icons/PhoneIcon';
import SearchInput  from '../SearchInput';
import UserLocation from '../UserLocation';

import { MenuItem, OverflowMenu } from './components';

function Header() {
  const { pathname } = useLocation();
  const { t }        = useTranslation();

  const navigation = useNavigation();
  const catalog    = useCatalog();
  const common     = useCommon();
  const sidebar    = useSidebar();
  const user       = useUser();

  const [count, setCount] = React.useState(0);
  const menuRef           = React.useRef(null);
  const topRef            = React.useRef(null);
  const toggleRef         = React.useRef(null);

  const bookmarksCount   = React.useMemo(() => {
    const items = user.bookmarks ?? {};

    return Object
      .keys(items)
      .reduce((count, itemKey) => {
        return items.hasOwnProperty(itemKey)
          ? count + Object.keys(items[itemKey]).length
          : count;
      }, 0);
  }, [user.bookmarks]);
  const dealerPage       = React.useMemo(() => {
    const dealer = common.pages.find((page) => {
      return page.template === 'dealer';
    });

    if (!dealer) {
      return;
    }

    return `/${dealer.urlKey}`;
  }, [common.pages]);
  const discountMenuItem = React.useMemo(() => {
    return navigation.menu.right.find((menuItem) => {
      return menuItem.label === t('admin_menu.discount');
    });
  }, [navigation.menu.right]);

  const handleClickItem    = React.useCallback((item) => {
    scrollTo({ top: 0 });

    if (needReset(`/${item.url}`)) {
      catalog.resetFilter();
      catalog.getCatalogItems(`/${item.url}`);
    }

    navigation.toggleState(false);
  }, []);
  const handleClickOutside = React.useCallback((event) => {
    // The toggle button handles its own click; otherwise the listener added
    // right after opening would catch the same click and close the menu at once
    if (toggleRef.current?.contains(event.target)) {
      return;
    }

    if (navigation.open && menuRef.current && !menuRef.current.contains(event.target)) {
      navigation.toggleState(false);
    }
  }, [navigation.open, menuRef.current]);
  const handleResize       = React.useCallback(() => {
    if (menuRef && topRef) {
      if (window.screen.width > 1152) {
        if (menuRef.current.clientWidth + topRef.current.clientWidth > window.innerWidth + 1) {
          setCount((prevCount) => {
            return Math.max(0, prevCount - 1);
          });
        }
      } else {
        setCount((prevCount) => {
          return Math.min(prevCount, navigation.menu.right.length);
        });
      }
    }
  }, [menuRef, topRef]);

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  React.useEffect(() => {
    if (navigation.open) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [navigation.open, handleClickOutside]);

  return (
    <div className={'header_wrapper'}>
      <nav
        className={classNames('header_nav', {
          open:        navigation.open,
          transparent: pathname === '/',
        })}
        id={'nav'}
      >
        <div
          className={'menu-mobile'}
          ref={menuRef}
        >
          <div className={'menu-header_top_container'}>
            <div className={'menu-header'}>
              <NavLink
                className={'logo'}
                onClick={() => {
                  navigation.toggleState(false);
                }}
                to={'/'}
              ><span className={'ic-logo-large'} /></NavLink>
              <span
                className={'ic-menu'}
                onClick={() => {
                  navigation.toggleState(!navigation.open);
                }}
              />
            </div>
            <div className={'left'}>
              <ul>
                {navigation.menu.left?.map((menuLeftItem, menuLeftItemIndex) => {
                  return (
                    <MenuItem
                      expand={navigation.expand}
                      handleExpand={navigation.expandSection}
                      item={menuLeftItem}
                      key={menuLeftItemIndex}
                      onClick={handleClickItem}
                    />
                  );
                })}
              </ul>
            </div>
            <div className={'right'}>
              {!!navigation.menu.right?.length && (
                <>
                  <ul>
                    {navigation.menu.right
                      .slice(0, count)
                      .map((menuRightItem, menuRightItemIndex) => (
                        <MenuItem
                          expand={navigation.expand}
                          handleExpand={navigation.expandSection}
                          item={menuRightItem}
                          key={menuRightItemIndex}
                          onClick={handleClickItem}
                        />
                      ))
                    }
                    {!!dealerPage && (
                      <li>
                        <NavLink
                          onClick={() => {
                            navigation.toggleState(false);
                          }}
                          to={dealerPage}
                        >{t('client_dealer')}</NavLink>
                      </li>
                    )}
                  </ul>
                  <OverflowMenu>
                    {navigation.menu.right
                      .slice(count)
                      .map((menuRightItem, menuRightItemIndex) => {
                        return (
                          <MenuItem
                            key={menuRightItemIndex}
                            item={menuRightItem}
                            onClick={handleClickItem}
                            expand={navigation.expand}
                            handleExpand={navigation.expandSection}
                          />
                        );
                      })
                    }
                  </OverflowMenu>
                </>
              )}
            </div>
          </div>
          <div className={'social'}>
            <a
              href={'https://www.youtube.com/channel/UCmks1HoZfGCLtcYfeT6gNvA'}
              rel={'nofollow'}
              target={'_blank'}
            ><span className={'ic-ut'} /></a>
            <a
              href={'https://vk.com/steellineofficial'}
              rel={'nofollow'}
              target={'_blank'}
            ><span className="ic-vk-mono" /></a>
            <a
              href={'https://www.facebook.com/steellineofficial/'}
              rel={'nofollow'}
              target={'_blank'}
            ><span className={'ic-fb'} /></a>
            <a
              href={'https://www.instagram.com/steellineofficial/'}
              rel={'nofollow'}
              target={'_blank'}
            ><span className={'ic-inst'} /></a>
          </div>
        </div>
        <div className={'header_menu'}>
          <NavLink
            className={'logo'}
            to={'/'}
          ><span className={'ic-logo'} /></NavLink>
          <div className={'menu-header'} />
          <div className={'left'}>
            <ul className={'nav_container'}>
              {navigation.menu.left?.map((menuLeftItem, menuLeftItemIndex) => {
                return (
                  <MenuItem
                    className={classNames('btn', 'fill')}
                    expand={navigation.expand}
                    handleExpand={navigation.expandSection}
                    item={menuLeftItem}
                    key={menuLeftItemIndex}
                    onClick={handleClickItem}
                  />
                );
              })}
              <li><SearchInput /></li>
            </ul>
          </div>
        </div>
        <div
          className={'top'}
          ref={topRef}
        >
          <span
            className={'ic-menu'}
            onClick={() => {
              navigation.toggleState(!navigation.open);
            }}
            ref={toggleRef}
          />
          <ul className={'top_bnts_wrapper'}>
            <li>
              <NavLink
                className={'bookmarks_wrapper'}
                to={'/izbrannoe'}
              >
                <span className={'ic-like-fill'} />
                <span className={'bookmarks_title'}>{t('client_header_bookmarks')}</span>
                <span
                  className={'badge'}
                  style={{ background: bookmarksCount === 0 ? '#bcbcbc' : '#488ec8' }}
                >{bookmarksCount}</span>
              </NavLink>
            </li>
            <li
              className={'contact_wrapper'}
              onClick={() => {
                sidebar.showSidebar(sidebarConstants.CALL_ME_SIDEBAR);
              }}
            >
              <PhoneIcon />
              <span className={'contact_title'}>{t('client_contact')}</span>
            </li>
          </ul>
        </div>
      </nav>
      <header
        className={classNames({ transparent: pathname === '/' })}
        id={'header'}
      >
        <div className={'header_btns'}>
          <UserLocation />
          <div className={'right'}>
            {!!navigation.menu.right?.length && (
              <>
                <ul>
                  {navigation.menu.right
                    .slice(0, count)
                    .map((menuRightItem, menuRightItemIndex) => {
                      return (
                        <MenuItem
                          expand={navigation.expand}
                          handleExpand={navigation.expandSection}
                          item={menuRightItem}
                          key={menuRightItemIndex}
                          onClick={handleClickItem}
                        />
                      );
                    })
                  }
                </ul>
                <OverflowMenu>
                  {navigation.menu.right
                    .slice(count)
                    .map((menuRightItem, menuRightItemIndex) => {
                      return (
                        <MenuItem
                          expand={navigation.expand}
                          handleExpand={navigation.expandSection}
                          item={menuRightItem}
                          key={menuRightItemIndex}
                          onClick={handleClickItem}
                        />
                      );
                    })}
                </OverflowMenu>
              </>
            )}
          </div>
          <div className={'btns_wrapper'}>
            {!!dealerPage && (
              <NavLink
                className={'dealer_wrapper'}
                to={dealerPage}
              >
                <DealerIcon />
                <p>{t('client_become_dealer')}</p>
              </NavLink>
            )}
            <NavLink
              className={'discount_wrapper'}
              to={`/${discountMenuItem?.url}`}
            >
              <DiscountIcon />
              <p>{discountMenuItem?.label}</p>
            </NavLink>
          </div>
        </div>
        <ul className={'mobile_btns'}>
          {navigation.menu.left?.map((menuLeftItem, menuLeftItemIndex) => {
            return (
              <MenuItem
                className={classNames('btn', 'fill')}
                expand={navigation.expand}
                handleExpand={navigation.expandSection}
                item={menuLeftItem}
                key={menuLeftItemIndex}
                onClick={handleClickItem}
              />
            );
          })}
        </ul>
      </header>
    </div>
  );
}

export default Header;
